import { useCallback, useMemo } from 'react';
import { type UserSetting } from '@soulhx/fs-common';
import { getDefaultSettingObj } from '@soulhx/user-settings';
import { db, type LocalUserSetting } from '../model/db.js';
import { queueLocalSettingUpdate } from '../sync/SettingSync.js';
import { useAsyncLiveQuery } from './useAsyncLiveQuery.js';

export const useUserSetting = <Type extends string | boolean>(
  settingName: string
): { settingValue: Type; update(newVal: Type): Promise<void>; isLoading: boolean } => {
  const { data: setting, isLoading } = useAsyncLiveQuery<UserSetting>(
    async () => {
      let dbSetting: LocalUserSetting | undefined;

      try {
        dbSetting = await db.settings.get(settingName);
      } catch {
        // do nothing
      }

      if (!dbSetting) {
        return getDefaultSettingObj(settingName);
      }

      return {
        settingId: settingName,
        settingBoolValue: dbSetting.settingBoolValue,
        settingStringValue: dbSetting.settingStringValue,
      };
    },
    [settingName],
    getDefaultSettingObj(settingName)
  );

  const getTypeFromObj = useCallback((setting: UserSetting) => {
    if (setting.settingBoolValue !== undefined) {
      return setting.settingBoolValue as Type;
    }

    return setting.settingStringValue as Type;
  }, []);

  const val = useMemo(() => {
    return getTypeFromObj(setting);
  }, [getTypeFromObj, setting, settingName]);

  const update = useCallback(
    async (newVal: Type) => {
      if (newVal === val) return;

      const newSetting: UserSetting = { settingId: settingName };
      if (typeof newVal === 'boolean') {
        newSetting.settingBoolValue = newVal as boolean;
      } else {
        newSetting.settingStringValue = newVal as string;
      }

      await queueLocalSettingUpdate(newSetting);
    },
    [val, settingName]
  );

  return { settingValue: val, update, isLoading };
};
